import Reveal from "@/components/Reveal";
import { PILLARS } from "@/constants/testIds";
import {
  Sparkles,
  Sprout,
  HeartPulse,
  Sun,
  Compass,
  Users,
  Scale,
  Palette,
  Mountain,
} from "lucide-react";

const pillars = [
  {
    slug: "presence",
    label: "Presence",
    copy: "Be here. Fully. The moment is the only place you truly live.",
    Icon: Sparkles,
    span: "md:col-span-2 md:row-span-2",
    tone: "sand",
  },
  {
    slug: "growth",
    label: "Growth",
    copy: "Small, deliberate steps become the shape of a life.",
    Icon: Sprout,
    span: "md:col-span-1",
    tone: "clay",
  },
  {
    slug: "health",
    label: "Health",
    copy: "Move your body. Feed your mind. Honor your rest.",
    Icon: HeartPulse,
    span: "md:col-span-1",
    tone: "earth",
  },
  {
    slug: "faith",
    label: "Faith",
    copy: "Anchored in something greater. Grounded in belief.",
    Icon: Sun,
    span: "md:col-span-2",
    tone: "moss",
  },
  {
    slug: "purpose",
    label: "Purpose",
    copy: "Chase what matters. Leave the rest behind.",
    Icon: Compass,
    span: "md:col-span-1",
    tone: "earth",
  },
  {
    slug: "community",
    label: "Community",
    copy: "You become who you walk with. Choose wisely.",
    Icon: Users,
    span: "md:col-span-1",
    tone: "clay",
  },
  {
    slug: "balance",
    label: "Balance",
    copy: "Between ambition and ease. Between doing and being.",
    Icon: Scale,
    span: "md:col-span-1",
    tone: "terracotta",
  },
  {
    slug: "creativity",
    label: "Creativity",
    copy: "Make something honest. Every day.",
    Icon: Palette,
    span: "md:col-span-1",
    tone: "earth",
  },
  {
    slug: "adventure",
    label: "Adventure",
    copy: "Go where you have never been — outward, and inward.",
    Icon: Mountain,
    span: "md:col-span-2",
    tone: "clay",
  },
];

const toneStyles = {
  sand: "bg-[#c9d3d8] text-[#0a0a0a]",
  clay: "bg-[#1a1f24] text-[#e8ecef] border border-[#1a2028]",
  earth: "bg-[#1a2028] text-[#e8ecef]",
  moss: "bg-[#1f3d31] text-[#e8ecef]",
  terracotta: "bg-[#14304a] text-[#e8ecef]",
};

export default function Pillars() {
  return (
    <section
      id="pillars"
      data-testid={PILLARS.root}
      className="relative py-32 md:py-48"
      style={{
        backgroundImage:
          "linear-gradient(to bottom, #14242e 0%, #101318 18%, #101318 82%, #0f2231 100%)",
      }}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-20 md:mb-28">
          <div>
            <Reveal>
              <p className="text-[11px] tracking-[0.38em] uppercase text-[#8a9498] mb-8">
                (02) — The Pillars
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#e8ecef] max-w-3xl">
                Nine ways to<br />
                <span className="font-light text-[#8a9498]">
                  move with intention.
                </span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-[#8a9498] max-w-sm leading-relaxed font-light">
              A quiet framework for a life lived on purpose — the foundation
              behind every VISION drop, journal, and gathering.
            </p>
          </Reveal>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[minmax(220px,_auto)]">
          {pillars.map((p, i) => {
            const { Icon } = p;
            return (
              <Reveal
                key={p.slug}
                delay={i * 0.04}
                className={`${p.span} min-h-[220px]`}
              >
                <div
                  data-testid={PILLARS.card(p.slug)}
                  className={`group relative h-full w-full ${toneStyles[p.tone]} p-8 md:p-10 flex flex-col justify-between overflow-hidden transition-all duration-500 hover:-translate-y-1`}
                >
                  <div className="flex items-start justify-between">
                    <Icon
                      strokeWidth={1}
                      className="w-8 h-8 md:w-10 md:h-10 opacity-80"
                    />
                    <span className="text-[10px] tracking-[0.3em] opacity-50">
                      0{i + 1}
                    </span>
                  </div>
                  <div className="mt-10">
                    <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-none tracking-tight">
                      {p.label}
                    </h3>
                    <p className="mt-4 text-sm md:text-[15px] leading-relaxed opacity-75 max-w-[28ch] font-light">
                      {p.copy}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
